/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { connect } from "react-redux";
import PropTypes from "prop-types";

// @material-ui core components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import FormSelector from "components/FormSelector";

import { useTranslation } from "react-i18next";
import { Field, reduxForm } from "redux-form";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import MDTypography from "components/MDTypography";

// Settings page components
import FormField from "components/FormField";
import MDAvatar from "components/MDAvatar";
// Data
import selectData from "layouts/account/components/BasicInfo/data/selectData";
import { Icon, Modal } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import { useEffect, useState } from "react";
import {
  updateMainUserEmail,
  uploadMainUserImage,
  deleteMainUserImage,
} from "store/actions/mainUserActions";
import { newAlertMessage } from "store/actions/globalAlertActions";
import EmailEditForm from "./emailEditForm";

const validate = (values) => {
  const errors = {};
  const requiredFields = [
    "firstName",
    "lastName",
    "email",
    "address",
    "city",
    "postcode",
    "language",
    "phoneNumber",
    "company",
    "vatNumber",
  ];
  requiredFields.forEach((field) => {
    if (!values[field]) {
      errors[field] = "Required";
    }
  });
  if (values.email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)) {
    errors.email = "Invalid email address";
  }
  return errors;
};

function BasicInfo({
  handleSubmit,
  pristine,
  onUpdateMainUserEmail,
  user,
  database,
  actNewAlertMessage,
  actSendMainUserImage,
  actSendDeleteMainUserImage,
}) {
  const { t } = useTranslation();
  const [openEditEmail, setOpenEditEmail] = useState(false);
  const [imageData, setImageData] = useState();
  const [hasAvatarLoadError, setHasAvatarLoadError] = useState(false);

  const { uid } = user;

  const { imageUrl = `https://onnaliving.com/userimagefiles/${uid}.png` } = user;
  const avatarSrc = imageData || imageUrl || "";
  const resolvedAvatarSrc = hasAvatarLoadError ? "" : avatarSrc;

  useEffect(() => {
    setHasAvatarLoadError(false);
  }, [avatarSrc]);

  const editDataAndSubmit = async (data) => {
    try {
      await database.editBasicInfoMainUser(data);
      actNewAlertMessage({
        type: "success",
        title: t("Basic Info"),
        content: `${t("configurationSent")}`,
      });
    } catch (error) {
      actNewAlertMessage({
        type: "error",
        title: t("Basic Info"),
        content: `${t("Save")} ${t("failed")}`,
      });
    }
  };

  const imageUrlToSend = `https://onnaliving.com/userimagefiles/${uid}.png?${Date.now()}`;

  const submitImage = () => {
    database.editImageMainUser(imageUrlToSend);
  };

  const deleteImage = () => {
    setImageData(null);
    database.deleteImageMainUser();
  };

  const onChangeImage = (data) => {
    if (data.target.files[0]) {
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        const image = new Image();
        image.src = reader.result;
        image.onload = () => {
          const { width, height } = image;
          if (width === 256 && height === 256) {
            setImageData(reader.result);
          } else {
            actNewAlertMessage({
              type: "warning",
              title: "image",
              content: "wrongSize",
            });
          }
        };
      });
      reader.readAsDataURL(data.target.files[0]);
    }
  };

  return (
    <form onSubmit={handleSubmit(editDataAndSubmit)}>
      <Card id="basic-info" sx={{ overflow: "visible" }}>
        <Modal
          open={openEditEmail}
          onClose={() => {
            setOpenEditEmail(false);
          }}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          style={{ overflow: "scroll" }}
        >
          <MDBox
            mt={3}
            sx={12}
            sm={8}
            style={{
              position: "absolute",
              width: "65vw",
              left: "50%",
              transform: "translate(-50%, 0%)",
            }}
          >
            <EmailEditForm
              onSubmit={(data) => {
                setOpenEditEmail(false);
                const { newEmail } = data;
                if (newEmail) {
                  onUpdateMainUserEmail({ newEmail, oldEmail: user.email });
                }
              }}
            />
          </MDBox>
        </Modal>
        <MDBox p={3}>
          <MDTypography variant="h5">{t("Basic Info")}</MDTypography>
        </MDBox>
        <MDBox pb={3} px={3}>
          <Grid container spacing={3}>
            <Grid
              size={{ xs: 12, sm: 12 }}
              display="flex"
              flexDirection="column"
              alignItems="center"
            >
              <MDBox position="relative">
                <input
                  style={{
                    opacity: "0.0",
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    zIndex: 2,
                  }}
                  id="profile-img-file-input"
                  type="file"
                  accept="image/png"
                  onChange={onChangeImage}
                />
                <MDAvatar
                  style={{ zIndex: 1 }}
                  alt={resolvedAvatarSrc ? "profile-image" : ""}
                  src={resolvedAvatarSrc}
                  imgProps={{
                    onError: () => {
                      setHasAvatarLoadError(true);
                    },
                  }}
                  size="xl"
                  shadow="sm"
                  bgColor="secondary"
                >
                  {!resolvedAvatarSrc && <PersonIcon sx={{ fontSize: 42 }} />}
                </MDAvatar>
              </MDBox>
              <MDBox>
                <MDButton
                  style={{ marginTop: 10 }}
                  size="small"
                  variant="contained"
                  component="label"
                  startIcon={<Icon>upload</Icon>}
                  onClick={() => {
                    actSendMainUserImage({
                      image: imageData,
                      cb: (result) => {
                        if (result) {
                          actNewAlertMessage({
                            type: "success",
                            title: "image",
                            content: `${t("uploadFile")} ${t("Success")}`,
                          });
                          submitImage();
                        } else {
                          actNewAlertMessage({
                            type: "error",
                            title: "image",
                            content: `${t("uploadFile")} ${t("unknownError")}`,
                          });
                        }
                      },
                    });
                  }}
                >
                  {t("uploadFile")}
                </MDButton>
                <MDButton
                  style={{ marginTop: 10 }}
                  size="small"
                  variant="contained"
                  component="label"
                  startIcon={<Icon>delete</Icon>}
                  onClick={() => {
                    actSendDeleteMainUserImage({
                      cb: (result) => {
                        if (result) {
                          actNewAlertMessage({
                            type: "success",
                            title: "image",
                            content: `${t("Delete")} ${t("Success")}`,
                          });
                          deleteImage();
                        } else {
                          actNewAlertMessage({
                            type: "error",
                            title: "image",
                            content: `${t("Delete")} ${t("unknownError")}`,
                          });
                        }
                      },
                    });
                  }}
                >
                  {t("Delete")}
                </MDButton>
              </MDBox>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Field name="firstName" component={FormField} label={t("First Name")} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Field name="lastName" component={FormField} label={t("Last Name")} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }} display="flex" flexDirection="row" alignItems="center">
              <Field name="email" component={FormField} label="Email" disabled />
              <MDBox marginLeft={1}>
                <MDButton
                  variant="gradient"
                  onClick={() => {
                    setOpenEditEmail(true);
                  }}
                  color="info"
                  iconOnly
                >
                  <Icon>edit</Icon>
                </MDButton>
              </MDBox>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Field name="address" component={FormField} label={t("Address")} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Field name="city" component={FormField} label={t("City")} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Field name="postcode" component={FormField} label={t("Postcode")} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Field name="phoneNumber" component={FormField} label={t("Phone Number")} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Field name="company" component={FormField} label={t("Company")} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Field name="vatNumber" component={FormField} label={t("VAT Number")} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Field
                name="language"
                values={selectData.languageValues}
                names={selectData.languageNames}
                component={FormSelector}
                label={t("Language")}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <MDBox display="flex" justifyContent="center" mt={2} xs={12}>
                <MDButton
                  type="submit"
                  variant="gradient"
                  color="dark"
                  size="small"
                  disabled={pristine}
                >
                  {t("Save")}
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </Card>
    </form>
  );
}

BasicInfo.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  database: PropTypes.object.isRequired,
  onUpdateMainUserEmail: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  actNewAlertMessage: PropTypes.func.isRequired,
  actSendMainUserImage: PropTypes.func.isRequired,
  actSendDeleteMainUserImage: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  const { mainUserReducer } = state;

  const initialValues = {
    firstName: mainUserReducer.firstName,
    lastName: mainUserReducer.lastName,
    email: mainUserReducer.email,
    address: mainUserReducer.address,
    city: mainUserReducer.city,
    postcode: mainUserReducer.postcode,
    phoneNumber: mainUserReducer.phoneNumber,
    company: mainUserReducer.company,
    vatNumber: mainUserReducer.vatNumber,
    language: mainUserReducer.language,
  };
  return {
    initialValues,
    user: state.mainUserReducer,
  };
};

const mapDispatchToProps = (dispatch) => ({
  actNewAlertMessage: (message) => dispatch(newAlertMessage(message)),
  actSendMainUserImage: (data) => dispatch(uploadMainUserImage(data)),
  actSendDeleteMainUserImage: (data) => dispatch(deleteMainUserImage(data)),
  onUpdateMainUserEmail: (data) => {
    dispatch(updateMainUserEmail(data));
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  reduxForm({
    form: "AccountBasicInfo", // a unique identifier for this form
    enableReinitialize: true,
    validate,
  })(BasicInfo)
);
